import React, { useCallback, useEffect, useState } from 'react';
import portAerial from '../../assets/ships/port-aerial.jpg';
import shipsDock from '../../assets/ships/ships-dock.jpg';
import shipMaersk from '../../assets/ships/ship-maersk.jpg';
import shipMsc from '../../assets/ships/ship-msc.jpg';
import seaOpen from '../../assets/ships/sea.jpg';
import seaCalm from '../../assets/ships/sea-calm.jpg';
import seaTanker from '../../assets/ships/sea-tanker-aerial.png';

export type FleetSlide = {
  id: number;
  src: string;
  title: string;
  subtitle: string;
  tag: string;
};

export const fleetSlides: FleetSlide[] = [
  {
    id: 1,
    src: portAerial,
    title: 'Port & Harbor Operations',
    subtitle: 'Coordinated vessel movements across Egyptian ports and terminals',
    tag: 'Operations',
  },
  {
    id: 2,
    src: shipsDock,
    title: 'Fleet at Dock',
    subtitle: 'Large vessels ready for charter, rental, and offshore support',
    tag: 'Fleet',
  },
  {
    id: 3,
    src: shipMaersk,
    title: 'Container Vessels',
    subtitle: 'Global-scale cargo ships for international maritime transport',
    tag: 'Charter',
  },
  {
    id: 4,
    src: shipMsc,
    title: 'Deep-Sea Cargo',
    subtitle: 'Professional container operations on open ocean routes',
    tag: 'Cargo',
  },
  {
    id: 5,
    src: seaTanker,
    title: 'Tanker & Bulk Fleet',
    subtitle: 'Aerial fleet overview — precision navigation in open waters',
    tag: 'Fleet',
  },
  {
    id: 6,
    src: seaOpen,
    title: 'Offshore Support',
    subtitle: 'Reliable vessel deployment for maritime projects in Egypt',
    tag: 'Support',
  },
  {
    id: 7,
    src: seaCalm,
    title: 'Coastal Waters',
    subtitle: 'Calm-sea operations supporting safe offshore project delivery',
    tag: 'Marine',
  },
];

const AUTO_MS = 5500;

const FleetCarousel: React.FC = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [animating, setAnimating] = useState(false);
  const total = fleetSlides.length;

  const goTo = useCallback(
    (index: number) => {
      if (animating) return;
      setAnimating(true);
      setActive(((index % total) + total) % total);
      window.setTimeout(() => setAnimating(false), 600);
    },
    [animating, total]
  );

  const next = useCallback(() => goTo(active + 1), [active, goTo]);
  const prev = useCallback(() => goTo(active - 1), [active, goTo]);

  useEffect(() => {
    if (paused) return;
    const id = window.setInterval(next, AUTO_MS);
    return () => window.clearInterval(id);
  }, [next, paused]);

  const slide = fleetSlides[active];
  const prevIdx = (active - 1 + total) % total;
  const nextIdx = (active + 1) % total;

  return (
    <div
      className="marine-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="marine-carousel-stage">
        {/* Side peek slides */}
        <button
          type="button"
          className="marine-carousel-peek marine-carousel-peek--left group"
          onClick={prev}
          aria-label="Previous slide"
        >
          <img src={fleetSlides[prevIdx].src} alt="" />
          <span className="marine-carousel-peek-shade" />
          <span className="marine-carousel-peek-label">{fleetSlides[prevIdx].title}</span>
        </button>

        <button
          type="button"
          className="marine-carousel-peek marine-carousel-peek--right group"
          onClick={next}
          aria-label="Next slide"
        >
          <img src={fleetSlides[nextIdx].src} alt="" />
          <span className="marine-carousel-peek-shade" />
          <span className="marine-carousel-peek-label">{fleetSlides[nextIdx].title}</span>
        </button>

        {/* Main slide */}
        <div className={`marine-carousel-main ${animating ? 'is-changing' : ''}`}>
          <img
            key={slide.id}
            src={slide.src}
            alt={slide.title}
            className="marine-carousel-main-img"
          />
          <div className="marine-carousel-main-overlay" />
          <div className="marine-carousel-main-content">
            <span className="marine-carousel-tag">{slide.tag}</span>
            <h3 className="font-marine text-3xl md:text-4xl font-semibold text-white mb-2">
              {slide.title}
            </h3>
            <p className="text-blue-100/90 text-sm md:text-base max-w-lg">
              {slide.subtitle}
            </p>
            <div className="marine-carousel-counter">
              <span className="text-primary-300 font-marine text-2xl font-semibold">
                {String(active + 1).padStart(2, '0')}
              </span>
              <span className="text-white/40 mx-2">/</span>
              <span className="text-white/50 font-marine text-lg">
                {String(total).padStart(2, '0')}
              </span>
            </div>
          </div>
        </div>

        {/* Arrow controls */}
        <button
          type="button"
          className="marine-carousel-arrow marine-carousel-arrow--prev"
          onClick={prev}
          aria-label="Previous"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          type="button"
          className="marine-carousel-arrow marine-carousel-arrow--next"
          onClick={next}
          aria-label="Next"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Thumbnail strip */}
      <div className="marine-carousel-thumbs">
        {fleetSlides.map((s, i) => (
          <button
            key={s.id}
            type="button"
            className={`marine-carousel-thumb ${i === active ? 'is-active' : ''}`}
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}: ${s.title}`}
            aria-current={i === active ? 'true' : undefined}
          >
            <img src={s.src} alt="" />
            <span className="marine-carousel-thumb-label">{s.title}</span>
            <span className="marine-carousel-thumb-hover">
              <span className="marine-carousel-thumb-hover-title">{s.title}</span>
              <span className="marine-carousel-thumb-hover-desc">{s.subtitle}</span>
            </span>
          </button>
        ))}
      </div>

      {/* Progress bar */}
      <div className="marine-carousel-progress">
        <div
          className="marine-carousel-progress-bar"
          style={{ width: `${((active + 1) / total) * 100}%` }}
        />
      </div>
    </div>
  );
};

export default FleetCarousel;
